"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MandolinSocket = exports.SocketSubProtocol = exports.SocketState = exports.getChannelId = void 0;
const backo2_1 = __importDefault(require("backo2"));
const lodash_throttle_1 = __importDefault(require("lodash.throttle"));
const lodash_once_1 = __importDefault(require("lodash.once"));
const utils_1 = require("./utils");
const debug_1 = __importDefault(require("debug"));
const baseLogger = debug_1.default("mandolin");
const log = baseLogger.extend("socket");
const error = log.extend("error");
const logger = {
    log,
    error,
};
const MAX_PAYLOAD_SIZE = 128 * 1024;
const MESSAGE_FLUSH_INTERVAL_S = 0.2;
const KILL_CONNECTION_AFTER_MS = 180 * 1000; // 3 minutes
const PING_INTERVAL_MS = KILL_CONNECTION_AFTER_MS / 3;
function getChannelId(group, key) {
    return [group, key].join(":");
}
exports.getChannelId = getChannelId;
var SocketState;
(function (SocketState) {
    SocketState["UNKNOWN"] = "UNKNOWN";
    SocketState["CLOSED"] = "CLOSED";
    SocketState["CLOSING"] = "CLOSING";
    SocketState["CONNECTING"] = "CONNECTING";
    SocketState["OPEN"] = "OPEN";
})(SocketState = exports.SocketState || (exports.SocketState = {}));
const socketStateMap = {
    [WebSocket.CLOSED]: SocketState.CLOSED,
    [WebSocket.CLOSING]: SocketState.CLOSING,
    [WebSocket.CONNECTING]: SocketState.CONNECTING,
    [WebSocket.OPEN]: SocketState.OPEN,
};
var SocketSubProtocol;
(function (SocketSubProtocol) {
    SocketSubProtocol["FIREBASE"] = "firebaseToken";
    SocketSubProtocol["AUTH0"] = "auth0Token";
})(SocketSubProtocol = exports.SocketSubProtocol || (exports.SocketSubProtocol = {}));
/**
 * The functions below are [Type Guards](https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates)
 * used to help TypeScript determine the type of a given value.
 */
function isMessageBatch(message) {
    return message && message.type === "batch";
}
function isMessage(message) {
    return message && message.channelId !== undefined && message.data !== undefined;
}
function isPong(message) {
    return message && message.type === "pong";
}
function isErrorMessage(message) {
    return message && message.channelId === "error";
}
function isAwsErrorMessage(message) {
    return message && message.message && message.connectionId === "error";
}
class MandolinSocket {
    /**
     *
     * @param connectionString
     * @param getToken Method that returns token required to auth against the
     * websocket service
     * @param options MandolinSocketOptions
     */
    constructor(connectionString, getToken, { messageFlushInterval, batchingEnabled, tokenType } = {}) {
        this.connectionString = connectionString;
        this.batchingEnabled = false;
        this.subscriptions = new Map();
        this.lastMessageTime = 0;
        this.messageFlushInterval = MESSAGE_FLUSH_INTERVAL_S;
        // Messages that get grouped together until the next send interval.
        this.bufferedMessages = [];
        this.reconnectBackoff = new backo2_1.default({
            min: 1 * 1000,
            max: 10 * 1000,
            factor: 1.1,
        });
        /**
         * Throttled method used to send messages at an interval
         */
        this._throttledFlushMessages = lodash_throttle_1.default(this.flushImmediate, 1000 * this.messageFlushInterval, {
            leading: true,
        });
        if (typeof getToken === "string") {
            const token = getToken;
            getToken = () => token;
        }
        this.getToken = getToken;
        this.messageFlushInterval = messageFlushInterval || MESSAGE_FLUSH_INTERVAL_S;
        this.batchingEnabled = batchingEnabled || false;
        this.setTokenType(tokenType);
        if (this.batchingEnabled) {
            logger.log("WS Message batching is enabled");
        }
        // No need to wait on this to resolve since we'll send any queued messages on connect
        void this.setupSocket();
    }
    setTokenType(tokenType) {
        this.tokenType = tokenType || SocketSubProtocol.FIREBASE;
    }
    cleanupIntervals() {
        logger.log("Clearing intervals");
        clearInterval(this._staleCheckInterval);
        clearInterval(this._pingInterval);
    }
    setupSocket() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.socketState === SocketState.OPEN || this.socketState === SocketState.CONNECTING) {
                return;
            }
            logger.log("Attempting to connect to mandolin websocket");
            const protocols = [];
            const token = yield this.getToken();
            if (token) {
                protocols.push(this.tokenType);
                protocols.push(token);
            }
            try {
                this.socket = new WebSocket(this.connectionString, protocols);
                this.setupListeners();
            }
            catch (e) {
                logger.error("Error connecting to socket", e);
            }
        });
    }
    get socketState() {
        var _a;
        if (!socketStateMap[(_a = this.socket) === null || _a === void 0 ? void 0 : _a.readyState]) {
            return SocketState.UNKNOWN;
        }
        return socketStateMap[this.socket.readyState];
    }
    checkStaleConnection() {
        if (new Date().getTime() - this.lastMessageTime >= KILL_CONNECTION_AFTER_MS) {
            this.socket.close();
            this.reconnect();
        }
    }
    close() {
        this.cleanupIntervals();
        this.clearAllSubscriptions();
        this.clearBuffer();
        this.socket.close();
    }
    reconnect() {
        if (this._reconnectTimeout) {
            logger.log("reconnect called, but an attempt is already pending");
            return;
        }
        if (this.socketState === SocketState.OPEN || this.socketState === SocketState.CONNECTING) {
            this.reconnectBackoff.reset();
            clearTimeout(this._reconnectTimeout);
            this._reconnectTimeout = undefined;
            return;
        }
        const duration = this.reconnectBackoff.duration();
        logger.log(`Attempting connection. Will check back in ${duration} ms`);
        this.setupSocket();
        this._reconnectTimeout = setTimeout(() => {
            this._reconnectTimeout = undefined;
            this.reconnect();
        }, duration);
    }
    sendPing() {
        if (this.socketState === SocketState.OPEN) {
            this.bufferMessages([{ type: "ping" }]);
        }
    }
    onConnect() {
        logger.log("Socket connected");
        // Add checks for active connection
        this.cleanupIntervals();
        // connecting is considered evidence of an open connection
        this.lastMessageTime = new Date().getTime();
        this._staleCheckInterval = setInterval(this.checkStaleConnection.bind(this), 1000);
        this._pingInterval = setInterval(this.sendPing.bind(this), PING_INTERVAL_MS);
        // If we were disconnected, replay any messages
        this.replaySubscriptions();
        this.flushImmediate();
    }
    /**
     * Handler when a message is received on the socket. It handles
     * deserialization, batch processing, and triggering subscription callbacks
     */
    onMessage(event) {
        this.lastMessageTime = new Date().getTime();
        // No need to respond or process further
        if (event.data === "pong") {
            return;
        }
        // Attempt to decode the message
        let parsed;
        try {
            parsed = JSON.parse(event.data);
            logger.log("message received", parsed);
        }
        catch (e) {
            log(`Error parsing Websocket message, ignoring`, event.data);
        }
        let messages = [];
        if (isAwsErrorMessage(parsed)) {
            // probably rate-limited, so we need to-re-queue our subscriptions
            this.replaySubscriptions();
            logger.error(parsed);
        }
        else if (isMessageBatch(parsed)) {
            messages = parsed.messages.filter(isMessage);
            const errors = parsed.messages.filter((m) => !isMessage(m));
            if (errors.length > 0) {
                logger.error("Unrecognized messages", errors);
            }
        }
        else if (isMessage(parsed)) {
            messages = [parsed];
        }
        else if (!isPong(parsed)) {
            logger.error("Unrecognized message", parsed);
        }
        messages.forEach((message) => this.processSubscriptionCallbacks(message));
    }
    /**
     * Adds required listeners/handlers to the websocket connection
     */
    setupListeners() {
        this.socket.addEventListener("open", this.onConnect.bind(this));
        this.socket.addEventListener("message", this.onMessage.bind(this));
        this.socket.addEventListener("error", (ev) => {
            logger.error("websocket error", ev);
            this.reconnect();
        });
        this.socket.addEventListener("close", () => {
            logger.log("close event recieved");
            this.reconnect();
        });
    }
    /**
     * Ensure all subscriptions are properly set up when a new connection is established
     */
    replaySubscriptions() {
        logger.log("replaySubscriptions", this.subscriptions.keys());
        const batch = [];
        for (const channelId of this.subscriptions.keys()) {
            const [group, key] = channelId.split(":");
            batch.push(this.makeSubscribePayload({ group, key }));
        }
        this.bufferMessages(batch);
    }
    /**
     * Executes all callbacks for a given message
     */
    processSubscriptionCallbacks(message) {
        if (isErrorMessage(message)) {
            logger.error(message.data.message);
            return;
        }
        const { channelId, data } = message;
        const subscriptions = this.subscriptions.get(channelId);
        if (subscriptions) {
            for (const cb of subscriptions.values()) {
                try {
                    cb(data);
                }
                catch (err) {
                    logger.error("Error processing message", message, err);
                }
            }
        }
    }
    /**
     * Unsubscribes the callback from
     */
    unsubscribe({ group, key }, subscriptionId) {
        logger.log(`unsubscribe ${group}:${key}`);
        const channelId = getChannelId(group, key);
        if (this.subscriptions.get(channelId)) {
            this.subscriptions.get(channelId).delete(subscriptionId);
            if (this.subscriptions.get(channelId).size === 0) {
                this.subscriptions.delete(channelId);
                // Only unsubscribe if we have no other listeners
                const message = { type: "unsubscribe", group, key };
                this.bufferMessages([message]);
            }
        }
    }
    /**
     * Subscribes to a given IChannel, ensuring the passed callback is executed
     * a matching message is received
     */
    subscribe({ group, key }, cb) {
        logger.log(`subscribe ${group}:${key}`);
        // Unique symbold to identify this subscription
        const subscriptionId = Symbol();
        this.bufferMessages([this.makeSubscribePayload({ group, key })]);
        const channelId = getChannelId(group, key);
        if (!this.subscriptions.get(channelId)) {
            this.subscriptions.set(channelId, new Map());
        }
        this.subscriptions.get(channelId).set(subscriptionId, cb);
        return lodash_once_1.default(() => {
            this.unsubscribe({ group, key }, subscriptionId);
        });
    }
    /**
     * Adds messages to the buffer. Messages are never sent directly, they are
     * always buffered, and then sent. This makes it easier to handle dealing with
     * disconnects.
     */
    bufferMessages(payloads) {
        payloads.forEach((payload) => this.bufferedMessages.push(payload));
        this.flushMessages();
    }
    /**
     * Immediately sends buffered messages
     */
    flushImmediate() {
        var _a;
        if (((_a = this.bufferedMessages) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            logger.log("No buffered messages to flush");
            return;
        }
        if (this.socketState !== SocketState.OPEN) {
            logger.log(`Can't flush messages, state is ${this.socketState}`);
            return;
        }
        setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            try {
                this._sendPayload(this.bufferedMessages);
                this.bufferedMessages = [];
            }
            catch (e) {
                logger.error("flushImmediate failed", e);
            }
        }), 50);
    }
    /**
     * Triggers sending buffered messages
     */
    flushMessages() {
        if (this.batchingEnabled) {
            this._throttledFlushMessages();
        }
        else {
            this.flushImmediate();
        }
    }
    /**
     * Internal methods for actually sending messages via the websocket connection
     * @param messages
     */
    _sendPayload(messages) {
        if (this.batchingEnabled) {
            const payload = JSON.stringify({
                type: "batch",
                messages,
            });
            const size = utils_1.byteSize(payload);
            if (size > MAX_PAYLOAD_SIZE) {
                logger.error("Payload too large, ignoring", size, payload);
            }
            this.socket.send(payload);
        }
        else {
            messages.forEach((message) => {
                this.socket.send(JSON.stringify(message));
            });
        }
    }
    /**
     * Correctly formats a payload for setting up a subscription
     */
    makeSubscribePayload({ group, key }) {
        return {
            type: "subscribe",
            group,
            key,
        };
    }
    /**
     * Removes all subscription listeners
     */
    clearAllSubscriptions() {
        this.subscriptions = new Map();
    }
    /**
     * Clears the message buffer
     */
    clearBuffer() {
        this.bufferedMessages = [];
    }
    makeSubscriber(group, globalKey) {
        const subscriber = (key, cb) => this.subscribe({
            group,
            key,
        }, cb);
        if (globalKey) {
            return (cb) => {
                return subscriber("global", cb);
            };
        }
        return subscriber;
    }
}
exports.MandolinSocket = MandolinSocket;
